/**
 * Client-side Customer.io fire helpers. Authenticates via Supabase session
 * and POSTs through the /api/customerio/* proxy. The actual SDK call (and
 * Person / Account identify) happens server-side.
 */

import { supabaseClient } from '~/shared/common/supabase/client'
import {
  type FireCustomerIOEventInput,
  type FireCustomerIOEventOutput,
} from '~/shared/customerio/customerio.api.schema'
import type { CustomerIOEventName } from '~/shared/customerio/customerio.types'

async function getBearer(): Promise<string | null> {
  const {
    data: { session },
  } = await supabaseClient.auth.getSession()
  return session?.access_token ?? null
}

/**
 * Fire a lifecycle event into Customer.io. Returns void; failures are logged
 * (best-effort write — never block the user-facing flow).
 */
export async function fireCustomerIOEvent(
  event: CustomerIOEventName,
  properties?: Record<string, unknown>,
): Promise<void> {
  try {
    const token = await getBearer()
    if (!token) return // unauthenticated fires are dropped silently

    const payload: FireCustomerIOEventInput = {
      event,
      ...(properties ? { properties } : {}),
    }

    const response = await fetch('/api/customerio/event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const body = await response.text().catch(() => '')
      console.error(`[customerio] /api/customerio/event ${response.status}: ${body}`)
      return
    }

    // Validate optimistically — we don't actually use the response
    const json = (await response.json().catch(() => ({}))) as FireCustomerIOEventOutput
    if (!json.success) console.warn('[customerio] non-success response')
  } catch (err) {
    console.error('[customerio] fire failed:', err instanceof Error ? err.message : err)
  }
}

/** Re-identify the current user (Person + Account). Best-effort; never throws. */
export async function identifyCustomerIOClient(): Promise<void> {
  try {
    const token = await getBearer()
    if (!token) return
    await fetch('/api/customerio/identify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
  } catch (err) {
    console.error('[customerio] identify failed:', err instanceof Error ? err.message : err)
  }
}
